import { useState, useRef, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { overAllSummaryColumns } from "../../constants/table-columns/krogerReportTableColumns";
import * as constants from "../../constants/constants";
import styles from "./KrogerReport.module.scss";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { getOverAllSummary } from "../../services/KrogerReportService";
import Spinner from "../../components/spinner/spinner";
import { Toast } from "primereact/toast";
import { formatInTimeZone } from "date-fns-tz";

const OverallSummary = ({ dateRange }) => {
  const toast = useRef();
  const [excludedUnits, setExcludedUnits] = useState([]);
  const [data, setData] = useState([]);
  const [grandKitchenTotal, setGrandKitchenTotal] = useState(null);
  const [expandedCards, setExpandedCards] = useState([]);
  const [pagination, setPagination] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);
  const { startDate, endDate } = dateRange;

  const BUSINESS_UNITS = [
    "Fresh Kitchen Deli",
    "Fresh Kitchen Produce",
    "Retail Produce"
  ];
  const convertToPacific = (date) => {
    if (!date) return null;
    return formatInTimeZone(
      new Date(date),
      "America/Los_Angeles",
      "yyyy-MM-dd'T'HH:mm:ss.SSS"
    );
  };
  useEffect(() => {
    if (!startDate || !endDate) {
      setData(null);
      setGrandKitchenTotal(null);
      return;
    }
    const fetchSummaryData = async () => {
      try {
        setShowSpinner(true);
        const payload = {
          startDate: convertToPacific(startDate),
          endDate: convertToPacific(endDate)
        };
        const response = await getOverAllSummary(payload);
        const apiCategories = response?.categories ?? [];
        setData(Array.isArray(apiCategories) ? apiCategories : []);
        setGrandKitchenTotal(response?.grandKitchenTotal ?? null);
        setShowSpinner(false);
      } catch (error) {
        setShowSpinner(false);
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.message,
          life: 4000
        });
        setData([]);
      }
    };
    fetchSummaryData();
  }, [startDate, endDate]);

  const hasData = Array.isArray(data) && data?.length > 0;
  const toggleUnit = (unit) =>
    setExcludedUnits((prev) =>
      prev.includes(unit) ? prev.filter((u) => u !== unit) : [...prev, unit]
    );
  const downloadMenu = useRef(null);

  const handleDownloadSummary = () => {
    // TODO: download summary logic
  };

  const handleDownloadAll = () => {
    // TODO: download all logic
  };

  const downloadItems = [
    {
      label: "Download Summary",
      icon: "pi pi-file",
      command: handleDownloadSummary
    },
    { label: "Download All", icon: "pi pi-box", command: handleDownloadAll }
  ];
  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2
    }).format(value ?? 0);

  const toggle = (name) =>
    setExpandedCards((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );

  const defaultCategories = [
    "Fresh Kitchen Deli",
    "Fresh Kitchen Produce",
    "Retail Produce"
  ].map((categoryName) => ({
    categoryName,
    standardCreditTotal: 0,
    totalCredits: 0,
    storeRows: [],
    totalsRow: {
      storeName: `${categoryName} Total`,
      standard: 0,
      vendor: 0,
      logistics: 0,
      go: 0,
      totalEdi: 0,
      total: 0
    },
    reversals: {
      vendor: 0,
      logistics: 0,
      goFees: 0,
      totalEdiReversals: 0
    }
  }));
  const defaultGrandKitchenTotal = {
    categoryName: "TF Grand Kitchen Total",
    standardCreditTotal: 0,
    totalCredits: 0,
    storeRows: [],
    totalsRow: {
      storeName: "TF Grand Kitchen Total",
      standard: 0,
      vendor: 0,
      logistics: 0,
      go: 0,
      totalEdi: 0,
      total: 0
    },
    reversals: {
      vendor: 0,
      logistics: 0,
      goFees: 0,
      totalEdiReversals: 0
    }
  };
  const categoriesToDisplay = hasData
    ? [...data, ...(grandKitchenTotal ? [grandKitchenTotal] : [])]
    : [...defaultCategories, defaultGrandKitchenTotal];

  return (
    <div>
      <Toast ref={toast} position="center" />
      {showSpinner && <Spinner />}
      <div>
        <div className={styles.filterBar}>
          {BUSINESS_UNITS?.map((unit) => {
            const active = !excludedUnits.includes(unit);
            return (
              <button
                key={unit}
                type="button"
                aria-pressed={active}
                className={`${styles.filterChip} ${active ? styles.filterChipActive : ""}`}
                onClick={() => toggleUnit(unit)}
              >
                <i className={`pi ${active ? "pi-check" : "pi-plus"}`} />
                {unit}
              </button>
            );
          })}
          <div className="ml-auto">
            <Menu
              model={downloadItems}
              popup
              ref={downloadMenu}
              id="download_menu"
              className={styles.downloadMenu}
            />
            <Button
              className={styles.downloadButton}
              onClick={(e) => downloadMenu.current.toggle(e)}
              aria-controls="download_menu"
              aria-haspopup
            >
              <i className="pi pi-download" />
              <span>Download</span>
              <i className="pi pi-chevron-down" />
            </Button>
          </div>
        </div>
        {categoriesToDisplay
          ?.filter(
            (category) => !excludedUnits.includes(category?.categoryName)
          )
          ?.map((category) => {
            const isRetailProduce = category?.categoryName === "Retail Produce";
            const isExpanded = expandedCards.includes(category?.categoryName);
            const storeRows = category?.storeRows ?? [];
            const totalsRow = category?.totalsRow ?? {
              storeName: `${category?.categoryName ?? ""} Total`,
              standard: 0,
              vendor: 0,
              logistics: 0,
              go: 0,
              totalEdi: 0,
              total: 0
            };
            const reversalItems = [
              {
                label: "Reversals – Vendor",
                value: category?.reversals?.vendor
              },
              {
                label: "Reversals – Logistics",
                value: category?.reversals?.logistics
              },
              {
                label: "Reversals – GO Fees",
                value: category?.reversals?.goFees
              }
            ];
            return (
              <div className={styles.card} key={category?.categoryName}>
                <div
                  className={styles.cardHeader}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggle(category?.categoryName)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(category?.categoryName);
                    }
                  }}
                >
                  <div className="flex justify-content-between align-items-center">
                    <h2 className={styles.title}>{category.categoryName}</h2>
                    <i
                      className={`pi ${
                        isExpanded ? "pi-chevron-up" : "pi-chevron-down"
                      } ${styles.chevron}`}
                    />
                  </div>
                  <div className={styles.summaryRow}>
                    <span>Standard Credit Total</span>
                    <span>{formatCurrency(category?.standardCreditTotal)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>Total Credits</span>
                    <span>{formatCurrency(category?.totalCredits)}</span>
                  </div>
                </div>
                {isExpanded && (
                  <div className="mt-2">
                    <div className={styles.reversalsBox}>
                      {reversalItems.map((item) => (
                        <div key={item.label} className={styles.summaryRow}>
                          <span>{item.label}</span>
                          <span>{formatCurrency(item.value)}</span>
                        </div>
                      ))}

                      <div className={`${styles.summaryRow} ${styles.bold}`}>
                        <span>Total EDI Reversals</span>
                        <span>
                          {formatCurrency(
                            category?.reversals?.totalEdiReversals
                          )}
                        </span>
                      </div>
                    </div>
                    <DataTable
                      value={storeRows}
                      tableStyle={{ minWidth: "50rem" }}
                      className={styles.overallDataGrid}
                      scrollable
                      first={pagination[category?.categoryName]?.first ?? 0}
                      rows={25}
                      onPage={(e) =>
                        setPagination((prev) => ({
                          ...prev,
                          [category?.categoryName]: {
                            first: e.first,
                            rows: e.rows
                          }
                        }))
                      }
                      paginator={constants?.paginator}
                      rowsPerPageOptions={constants?.rowsPerPageOptions}
                      paginatorTemplate={constants?.paginatorTemplate}
                      currentPageReportTemplate={
                        constants?.currentPageReportTemplate
                      }
                      emptyMessage={<></>}
                      dataKey="storeName"
                      scrollHeight="30vw"
                    >
                      <Column
                        field="storeName"
                        header="Store"
                        footer={totalsRow?.storeName ?? ""}
                      />
                      {(isRetailProduce
                        ? overAllSummaryColumns.filter(
                            (column) => column?.field === "total"
                          )
                        : overAllSummaryColumns
                      )?.map(({ field, header }) => (
                        <Column
                          key={field}
                          field={field}
                          header={header}
                          body={(row) => formatCurrency(row?.[field])}
                          footer={formatCurrency(totalsRow?.[field])}
                          alignHeader="right"
                          bodyClassName="text-right"
                          footerClassName="text-right"
                        />
                      ))}
                    </DataTable>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default OverallSummary;
